import {Tr, TrSpace} from "@/i18n/i18n";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {ManualLink} from "@/scenes.ui/manual-link";
import {VerticalLongPanel} from "@/scenes.ui/vertical-long-panel";
import PlayerStore from "@/store/player";
import {CampaignType, StatusDto} from "@/store/player/types";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import Scene = Phaser.Scene;
import BaseSound = Phaser.Sound.BaseSound;

export class CampaignChestPanel extends VerticalLongPanel {

    private shopCallback: (() => unknown) | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, clickSound: BaseSound | undefined, ct: CampaignType, shopCallback: () => unknown) {
        super(scene, canvas, Tr('c.chests'));

        this.shopCallback = shopCallback;
        let self = this;

        let silver = SpriteUtils.addSprite(scene, -56, -170, this.leftChest(ct), undefined, 0.75);
        let gold = SpriteUtils.addSprite(scene, 56, -175, this.rightChest(ct), undefined, 0.6);

        let silverText = TextUtils.addTextByFamily(scene, this.lowConds(ct).toUpperCase(), 0, -70,
            '#ffd465', 17, 'normal', 480, Fonts.MYRIADPRO_REGULAR);
        let silverManual = new ManualLink(scene, canvas, 0, -25, this.lowManualId(ct), this.lowDet(ct));

        let goldText = TextUtils.addTextByFamily(scene, this.highConds(ct).toUpperCase(), 0, 22,
            '#adffb3', 12, 'normal', 480, Fonts.MYRIADPRO_REGULAR);
        let goldManual = new ManualLink(scene, canvas, 0, 42, this.highManualId(ct),  this.highDet(ct), '#f9f1d9');

        this.add([gold, silver, silverText, silverManual, goldText, goldManual]);

        this.fetchFunc(ct)().then((s: StatusDto) => {
            let rulesText = TextUtils.addTextByFamily(scene, TrSpace('camp.no.more')
                + String(s.maxUsages) + (s.maxUsages == 1? Tr('tm.used') : Tr('tms.used')) + String(s.usages) + '/' + String(s.maxUsages) + ')', 0, 85,
                '#c2c2c2', 11, 'normal', 420, Fonts.MYRIADPRO_REGULAR);

            let dateText = TextUtils.addTextByFamily(scene, Tr('cmp.dates') + s.dates, 0, 115,
                '#c2c2c2', 11, 'normal', 420, Fonts.MYRIADPRO_REGULAR);

            self.add([rulesText, dateText]);
        });

        this.addCustomButton(-100, 197,Tr('zakrit'), Keys.UI_ART_BROWN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_DECLINE_CROSS, () => {
            SoundUtils.play(clickSound);
        });
        this.addCustomButton(100, 197, Tr('v.magazin').toUpperCase(), Keys.UI_ART_GREEN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, this.shopCallback);
    }

    private lowManualId(ct: CampaignType) {
        switch (ct) {
            case CampaignType.CHEST:
                return 'silverChest';
            case CampaignType.CHEST_2:
                return 'azureChest';
            case CampaignType.CHEST_3:
                return 'amberChest';
        }
        return 'azureChest';
    }

    private highManualId(ct: CampaignType) {
        switch (ct) {
            case CampaignType.CHEST:
                return 'goldenChest';
            case CampaignType.CHEST_2:
                return 'lavaChest';
            case CampaignType.CHEST_3:
                return 'jadeChest';
        }
        return 'lavaChest';
    }

    private lowConds(ct: CampaignType) {
        switch (ct) {
            case CampaignType.CHEST:
                return Tr('silv.conds');
            case CampaignType.CHEST_2:
                return Tr('az.conds');
            case CampaignType.CHEST_3:
                return Tr('amb.conds');
        }
        return Tr('az.conds');
    }

    private highConds(ct: CampaignType) {
        switch (ct) {
            case CampaignType.CHEST:
                return Tr('gold.conds');
            case CampaignType.CHEST_2:
                return Tr('lv.conds');
            case CampaignType.CHEST_3:
                return Tr('jd.conds');
        }
        return Tr('lv.conds');
    }

    private lowDet(ct: CampaignType) {
        switch (ct) {
            case CampaignType.CHEST:
                return Tr('silv.det');
            case CampaignType.CHEST_2:
                return Tr('az.det');
            case CampaignType.CHEST_3:
                return Tr('amb.det');
        }
        return Tr('az.det');
    }

    private highDet(ct: CampaignType) {
        switch (ct) {
            case CampaignType.CHEST:
                return Tr('gold.det');
            case CampaignType.CHEST_2:
                return Tr('lv.det');
            case CampaignType.CHEST_3:
                return Tr('jd.det');
        }
        return Tr('lv.det');
    }

    private leftChest(ct: CampaignType): string {
        switch (ct) {
            case CampaignType.CHEST:
                return Keys.CHEST_SILVER_CLOSED;
            case CampaignType.CHEST_2:
                return Keys.CHEST_AZURE_CLOSED;
            case CampaignType.CHEST_3:
                return Keys.CHEST_AMBER_CLOSED;
        }
        return Keys.CHEST_AZURE_CLOSED;
    }

    private rightChest(ct: CampaignType): string {
        switch (ct) {
            case CampaignType.CHEST:
                return Keys.CHEST_GOLDEN_CLOSED;
            case CampaignType.CHEST_2:
                return Keys.CHEST_LAVA_CLOSED;
            case CampaignType.CHEST_3:
                return Keys.CHEST_JADE_CLOSED;
        }
        return Keys.CHEST_LAVA_CLOSED;
    }

    private fetchFunc(ct: CampaignType): () => Promise<StatusDto> {
        switch (ct) {
            case CampaignType.CHEST:
                return PlayerStore.fetchChestCampaignStatus;
            case CampaignType.CHEST_2:
                return PlayerStore.fetchChest2CampaignStatus;
            case CampaignType.CHEST_3:
                return PlayerStore.fetchChest3CampaignStatus;
        }
        return PlayerStore.fetchChest2CampaignStatus;
    }

    protected onClose(): void {

    }

}
